import React, { useEffect, useMemo, useState } from "react";
import { Button, Chip, Grid, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import EntityHubLayout, { PAGE_SIZE } from "../../components/product-manager/EntityHubLayout";
import EntityAssetCard from "../../components/product-manager/EntityAssetCard";
import EditDocumentDialog from "../../components/product-manager/EditDocumentDialog";
import ViewDocumentDialog from "../../components/product-manager/ViewDocumentDialog";
import DocumentFileUploadDialog from "../../components/product-manager/DocumentFileUploadDialog";
import {
  filterByHubState,
  useBookmarkableHubState,
} from "../../hooks/useBookmarkableHubState";
import { useDocuments } from "../../hooks/useDocuments";
import { apiService } from "../../api/services/apiService";
import type { Document } from "../../types";
import { EntityStatus } from "../../types";
import { pmHubAddPath, PM_HUBS } from "../../utils/pmHubRoutes";
import { useAuth } from "../../contexts/AuthContext";
import { isProductionSupportAdmin, isProductManager } from "../../utils/appRoles";
import {
  OPERATOR_DOCUMENT_ENTITY_CLASSES,
  OPERATOR_DOCUMENT_CLASS_LABELS,
  documentMatchesTypeFilter,
  operatorDocumentClassLabel,
  ragIndexStatusLabel,
} from "../../utils/operatorDocuments";
import { isArchitectureStandardDocument } from "../../utils/architectureStandards";

const DocumentHubPage: React.FC = () => {
  const { user } = useAuth();
  const roles = user?.roles ?? [];
  const defaultOperatorFilter =
    isProductionSupportAdmin(roles) && !isProductManager(roles);
  const hubState = useBookmarkableHubState();
  const { documents: fetched, loading, error, refresh } = useDocuments();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [uploadMode, setUploadMode] = useState<"file" | "folder" | null>(null);
  const [viewUuid, setViewUuid] = useState<string | null>(null);
  const [toggleLoadingId, setToggleLoadingId] = useState<string | null>(null);
  const [entityClassFilter, setEntityClassFilter] = useState(
    defaultOperatorFilter ? "operator" : "all",
  );

  useEffect(() => {
    setDocuments(fetched);
  }, [fetched]);

  const filtered = useMemo(() => {
    const withoutStandards = filterByHubState(documents, hubState).filter(
      (d) => !isArchitectureStandardDocument(d),
    );
    if (entityClassFilter === "all") return withoutStandards;
    return withoutStandards.filter((d) => documentMatchesTypeFilter(d, entityClassFilter));
  }, [documents, hubState, entityClassFilter]);

  const paginated = useMemo(() => {
    const start = (hubState.page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, hubState.page]);

  const editDoc = useMemo(
    () => documents.find((d) => d.uuid === hubState.editUuid) ?? null,
    [documents, hubState.editUuid],
  );
  const viewDoc = useMemo(
    () => documents.find((d) => d.uuid === viewUuid) ?? null,
    [documents, viewUuid],
  );

  const mergeDocuments = (incoming: Document[]) => {
    setDocuments((prev) => {
      const incomingIds = new Set(incoming.map((d) => d.uuid));
      return [...incoming, ...prev.filter((d) => !incomingIds.has(d.uuid))];
    });
  };

  const handleToggle = async (doc: Document) => {
    setToggleLoadingId(doc.uuid);
    const newStatus =
      doc.status === EntityStatus.ACTIVE
        ? EntityStatus.INACTIVE
        : EntityStatus.ACTIVE;
    try {
      const updated = await apiService.updateDocument(doc.uuid, {
        name: doc.name,
        description: doc.description ?? undefined,
        status: newStatus,
      });
      setDocuments((prev) =>
        prev.map((d) => (d.uuid === updated.uuid ? updated : d)),
      );
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Failed to update status");
    } finally {
      setToggleLoadingId(null);
    }
  };

  return (
    <>
      <EntityHubLayout
        hubTitle="Document Hub"
        hubDescription="Manage SOP, runbook, and help-guide documents. Upload a file or folder; non-text files convert to Markdown before save as drafts."
        addPath={pmHubAddPath("document")}
        addLabel="Add Document"
        headerActions={
          <>
            <Button
              component={RouterLink}
              to={PM_HUBS.knowledgeSearch}
              variant="outlined"
              startIcon={<ManageSearchIcon />}
              sx={{ textTransform: "none" }}
            >
              Knowledge Search
            </Button>
            <Button
              variant="outlined"
              startIcon={<FolderOpenIcon />}
              onClick={() => setUploadMode("folder")}
              sx={{ textTransform: "none" }}
            >
              Upload from folder
            </Button>
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={() => setUploadMode("file")}
              sx={{ textTransform: "none" }}
            >
              Upload file
            </Button>
          </>
        }
        loading={loading}
        error={error}
        itemCount={filtered.length}
        hubState={hubState}
        filterSidebarExtra={
          <TextField
            select
            fullWidth
            size="small"
            label="Document type"
            value={entityClassFilter}
            onChange={(e) => setEntityClassFilter(e.target.value)}
            sx={{ mt: 2 }}
          >
            <MenuItem value="all">All types</MenuItem>
            <MenuItem value="operator">Operator (SOP / runbook / help-guide)</MenuItem>
            {OPERATOR_DOCUMENT_ENTITY_CLASSES.map((id) => (
              <MenuItem key={id} value={id}>
                {OPERATOR_DOCUMENT_CLASS_LABELS[id]}
              </MenuItem>
            ))}
            <MenuItem value="mixed">Mixed</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </TextField>
        }
      >
        <Grid container spacing={2}>
          {paginated.map((doc) => (
            <Grid item xs={12} sm={6} md={4} key={doc.uuid}>
              <EntityAssetCard
                title={doc.name}
                subtitle={doc.description ?? undefined}
                status={doc.status}
                metadata={
                  <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
                    <Chip size="small" label={operatorDocumentClassLabel(doc.entityClass)} />
                    <Chip size="small" variant="outlined" label={doc.status} />
                    <Chip size="small" variant="outlined" label={ragIndexStatusLabel(doc.ragIndexStatus)} />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={
                        doc.expirationDate
                          ? `Expires ${new Date(doc.expirationDate).toLocaleDateString()}`
                          : "No expiry"
                      }
                    />
                    {(doc.parents ?? []).map((parent) => (
                      <Chip
                        key={`${parent.entityType}-${parent.entityId}`}
                        size="small"
                        variant="outlined"
                        label={`${parent.entityType}: ${parent.name || parent.entityId}`}
                      />
                    ))}
                  </Stack>
                }
                enabled={doc.status === EntityStatus.ACTIVE}
                toggleLoading={toggleLoadingId === doc.uuid}
                onToggleEnabled={() => handleToggle(doc)}
                onView={() => setViewUuid(doc.uuid)}
                onEdit={() => hubState.setEditUuid(doc.uuid)}
                onDelete={async () => {
                  try {
                    await apiService.deleteDocument(doc.uuid);
                    setDocuments((prev) =>
                      prev.filter((d) => d.uuid !== doc.uuid),
                    );
                  } catch (err: unknown) {
                    alert(
                      err instanceof Error
                        ? err.message
                        : "Failed to delete document",
                    );
                  }
                }}
              />
            </Grid>
          ))}
          {!loading && filtered.length === 0 && (
            <Grid item xs={12}>
              <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                No documents match your filters.
              </Typography>
            </Grid>
          )}
        </Grid>
      </EntityHubLayout>

      <DocumentFileUploadDialog
        open={Boolean(uploadMode)}
        mode={uploadMode ?? "file"}
        onClose={() => setUploadMode(null)}
        onComplete={(created) => {
          mergeDocuments(created);
          void refresh();
          if (created.length === 1) {
            setViewUuid(created[0].uuid);
          }
        }}
      />

      <ViewDocumentDialog
        document={viewDoc}
        open={Boolean(viewUuid && viewDoc)}
        onClose={() => setViewUuid(null)}
        onEdit={() => {
          if (viewUuid) hubState.setEditUuid(viewUuid);
          setViewUuid(null);
        }}
      />

      <EditDocumentDialog
        document={editDoc}
        open={Boolean(hubState.editUuid && editDoc)}
        onClose={() => hubState.setEditUuid(null)}
        onSaved={(updated) =>
          setDocuments((prev) =>
            prev.map((d) => (d.uuid === updated.uuid ? updated : d)),
          )
        }
      />
    </>
  );
};

export default DocumentHubPage;
